import React, { useContext, useState, useEffect, useRef } from "react";
import { ListContext } from "context";
import { listContextDefaultChangeFetcherParams } from "context/List/Context";
import Select from "ui/Form/Select";

export type SortOption = {
    field: string;
    order?: "asc" | "desc";
    label: string;
};

export type SelectSortProps = {
    sortOptions: Array<SortOption>;
    withIconSort?: boolean;
    pager?: { order?: string; direction?: string; per_page?: number };
    activeFilters?: HashMap<unknown>;
    onChangeFetcherParams?: (params: HashMap<unknown>) => void;
};

const SelectSort: React.FC<SelectSortProps> = ({
    sortOptions,
    withIconSort,
    pager: pagerProp,
    activeFilters: activeFiltersProp,
    onChangeFetcherParams,
}) => {
    const listContext = useContext(ListContext);
    const warnedOutsideListProviderRef = useRef(false);

    const pager = pagerProp ?? listContext.data.pager;
    const activeFilters = activeFiltersProp ?? (listContext.data.active_filters as HashMap<unknown>);
    const changeFetcherParams = onChangeFetcherParams ?? listContext.changeFetcherParams;

    const [currentSort, setCurrentSort] = useState<string>(`${pager.order}-${pager.direction}`);

    useEffect(() => {
        setCurrentSort(`${pager.order}-${pager.direction}`);
    }, [pager]);

    if (
        onChangeFetcherParams === undefined &&
        listContext.changeFetcherParams === listContextDefaultChangeFetcherParams &&
        !warnedOutsideListProviderRef.current
    ) {
        warnedOutsideListProviderRef.current = true;
        console.warn(
            "[SelectSort] Relying on ListContext.changeFetcherParams, but no ListProvider is active (default no-op). " +
                "Wrap the tree with ListProvider or pass onChangeFetcherParams (and pager/activeFilters if needed).",
        );
    }

    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOption = sortOptions.find((option: SortOption) => {
            return `${option.field}-${option.order}` === e.target.value;
        });

        if (selectedOption) {
            setCurrentSort(e.target.value);
            changeFetcherParams(
                Object.assign({}, activeFilters, {
                    order: selectedOption.field,
                    direction: selectedOption.order,
                    per_page: pager.per_page,
                }),
            );
        } else {
            setCurrentSort("");
            changeFetcherParams({ order: "", direction: "", per_page: "" });
        }
    };

    return (
        <div className="crud-toolbar_sort">
            {withIconSort && <span className="icon icon-tri"></span>}
            <Select
                inputProps={{
                    name: "order_by",
                    options: sortOptions.map((option: SortOption) => {
                        return {
                            value: `${option.field}-${option.order}`,
                            label: option.label,
                        };
                    }),
                    value: currentSort,
                    onChange,
                }}
            />
            <span className="icon icon-arrow-right"></span>
        </div>
    );
};

export default SelectSort;
